import { useState } from 'react'
import {
  Grid,
  TextField,
  Autocomplete,
  FormControl,
  useTheme,
} from '@mui/material'
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { grey } from '@mui/material/colors'

import { useAppSelector } from 'redux/Hook'
import type { Project as ProjectType } from 'types/Project'
import { toggleFilterByProjectAndEngagementDialog } from 'redux/slices/FilterBuilder'
import { getEngagementText } from 'components/Participants/Filters/Project/Helper'
import CustomChip from 'components/Shared/Chip'
import { AdminUITheme } from 'types/AdminUiThemeMui'
import {
  zIndexLoweredFilterCSS,
  zIndexRaisedFilterCSS,
} from 'lib/GlobalConstants'

const FilterByProjectAndEngagementPills = (props: Props) => {
  const { condition } = props.item
  const theme: AdminUITheme = useTheme()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const projectsGlobalState = useAppSelector((state) => state.projects.data)

  const getSelectedProjects = () => {
    if (props.item.value) {
      const selectedProjects = props.item.value.split(',').map((id: string) => {
        const name = getProjectName(id)

        if (name) return { name, id }
      })

      // Remove undefined values, this can happen when a project is deleted after it is assigned
      const refinedProjects = selectedProjects.filter(
        (project: ProjectType) => project,
      )

      return refinedProjects
    }

    return []
  }

  const getProjectName = (id: string) => {
    const filteredProject = projectsGlobalState.find(
      (project: { id: string }) => project.id == id,
    ) as ProjectType

    return filteredProject?.name
  }

  const handleOpenModal = () => {
    dispatch(toggleFilterByProjectAndEngagementDialog())
  }

  const handleClick = () => dispatch(handleOpenModal())

  const EditIcon = (
    <ModeEditOutlinedIcon
      sx={{
        cursor: 'pointer',
        color: grey[600],
        fontSize: '1.25rem',
      }}
      onClick={handleClick}
    />
  )

  const renderAutocompletePill = (incomingCondition: Condition) => {
    const selectedProjects = getSelectedProjects()
    const [cssUpdates, setCssUpdates] = useState<any>(zIndexLoweredFilterCSS)
    const engagementText = t(
      `participant.projectAndEngagementLevel.${getEngagementText(
        incomingCondition,
      )}`,
    )
    return (
      <Autocomplete
        sx={{
          ...{
            '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
              pr: 3,
            },
          },
          ...theme.GenericTheme.dynamicFilterWrapperWidth,
        }}
        multiple
        options={selectedProjects}
        value={selectedProjects}
        limitTags={1}
        openText={t('filters.ariaTexts.open')}
        closeText={t('filters.ariaTexts.close')}
        clearText={t('filters.ariaTexts.clear')}
        data-cy={`${getEngagementText(incomingCondition)}_pill`}
        getOptionLabel={(option: { name: string }) => option.name}
        filterSelectedOptions
        readOnly // trick to not show the options dropdown
        renderTags={(tagValue: any[], getTagProps: any) => {
          return tagValue.map((option, index) => {
            return (
              <CustomChip
                key={option.name}
                filterValues={[option.name]}
                size="small"
                extraProps={{
                  ...getTagProps({ index }),
                  color: 'primary',
                }}
              />
            )
          })
        }}
        popupIcon={EditIcon}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={{
              ...theme.GenericTheme.textFieldCSSToRaiseZindex,
              ...cssUpdates,
              ...theme.GenericTheme.dynamicFilterWrapperWidth,
            }}
            label={engagementText}
            size="small"
          />
        )}
        onFocus={() => {
          setCssUpdates(zIndexRaisedFilterCSS)
        }}
        onBlur={() => {
          setCssUpdates(zIndexLoweredFilterCSS)
        }}
      />
    )
  }

  const handlePillsRendering = () => {
    const incomingCondition: Condition = condition
    return renderAutocompletePill(incomingCondition)
  }

  return (
    <Grid>
      <FormControl>{handlePillsRendering()}</FormControl>
    </Grid>
  )
}

export default FilterByProjectAndEngagementPills
